import React, { useState } from "react";
import { RECON_BONDS, TERM_OPTIONS, CREDIT_OPTIONS } from "./data.js";

export default function ReconStage({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [tags, setTags] = useState({});
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(null);

  const current = RECON_BONDS[index];
  const done = index >= RECON_BONDS.length;

  const chooseTag = (field, value) => {
    if (!current) return;
    const existing = tags[current.id] || {};
    const updated = { ...existing, [field]: value };
    setTags((prev) => ({ ...prev, [current.id]: updated }));

    if (updated.term && updated.credit) {
      const bothCorrect = updated.term === current.correctTerm && updated.credit === current.correctCredit;
      if (bothCorrect) setCorrectCount((c) => c + 1);
      setFlash(bothCorrect ? "correct" : "off");
      setTimeout(() => {
        setFlash(null);
        setIndex((i) => i + 1);
      }, 700);
    }
  };

  if (done) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-2 text-lime">Recon Complete</p>
        <h2 className="pixel-heading mb-4 text-base text-mist">Market Scouted</h2>

        <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-mist/60">Classified Correctly</span>
            <span className="text-lime">
              {correctCount}/{RECON_BONDS.length}
            </span>
          </div>
        </div>

        <p className="mx-auto mb-6 max-w-sm text-xs text-mist/60">
          The Golden Rule of Bonds: when interest rates rise, existing bond prices fall — and the
          longer the term, the harder they fall. Time to build a fortress that can take a hit.
        </p>

        <button onClick={() => onComplete({ correctCount })} className="btn-magenta">
          BUILD YOUR BASTION ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>RECON</span>
        <span>
          {index}/{RECON_BONDS.length} REVIEWED
        </span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Six real bond types. Tag each by how exposed it is to interest-rate moves, and how
        exposed it is to the issuer actually not paying you back.
      </p>

      <div
        key={current.id}
        className={`mb-5 border-2 border-black bg-ink p-5 transition-colors ${
          flash === "correct" ? "border-lime shadow-glow-cyan" : flash === "off" ? "border-danger" : ""
        }`}
      >
        <p className="pixel-heading mb-2 text-sm text-mist">{current.title}</p>
        <p className="text-xs leading-relaxed text-mist/70">{current.description}</p>
        {flash && <p className="mt-3 text-[11px] text-mist/50">{current.note}</p>}
      </div>

      <div className="mb-4">
        <p className="label-pixel mb-2 text-mist/60">Interest-Rate Risk (Term)</p>
        <div className="grid grid-cols-3 gap-2">
          {TERM_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.term === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("term", opt.id)}
                className={`border-2 px-2 py-2 font-display text-[8px] uppercase transition-colors ${
                  selected ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="label-pixel mb-2 text-mist/60">Credit Risk</p>
        <div className="grid grid-cols-3 gap-2">
          {CREDIT_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.credit === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("credit", opt.id)}
                className={`border-2 px-2 py-2 font-display text-[8px] uppercase transition-colors ${
                  selected ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
